import { cn } from '@/lib/utils';
import { Ship, Plane, Truck, Train, Package } from 'lucide-react';
import { type TransportMode, normalizeMode } from '@/components/common/transport-mode';

const modeStyles: Record<TransportMode, string> = {
  ocean: 'bg-mode-ocean-subtle text-mode-ocean-foreground border-mode-ocean-border',
  air: 'bg-mode-air-subtle text-mode-air-foreground border-mode-air-border',
  truck: 'bg-mode-truck-subtle text-mode-truck-foreground border-mode-truck-border',
  rail: 'bg-mode-rail-subtle text-mode-rail-foreground border-mode-rail-border',
  courier: 'bg-mode-courier-subtle text-mode-courier-foreground border-mode-courier-border',
};

const modeIcons: Record<TransportMode, typeof Ship> = {
  ocean: Ship,
  air: Plane,
  truck: Truck,
  rail: Train,
  courier: Package,
};

interface ModeBadgeProps {
  /** Raw mode string — normalized internally (OCEAN, ROAD_FTL, FCL, Air…). */
  mode?: string | null;
  /** Optional prefix rendered before the mode, e.g. "FCL" in "FCL · OCEAN". */
  prefix?: string;
  showIcon?: boolean;
  className?: string;
}

/**
 * Transport mode indicator. Use this rather than a bare Badge so modes stay
 * instantly recognisable across dense tables.
 */
export function ModeBadge({ mode, prefix, showIcon = true, className }: ModeBadgeProps) {
  const normalized = normalizeMode(mode);

  // Modes outside the five families (e.g. MULTIMODAL) still need to render —
  // returning null here would leave the field blank rather than showing the
  // value the record actually holds.
  if (!normalized) {
    if (!mode) return null;
    return (
      <span
        className={cn(
          'inline-flex items-center gap-1 rounded-md border px-2 py-0.5',
          'text-[11px] font-semibold uppercase tracking-wide',
          'bg-status-neutral-subtle text-status-neutral-foreground border-status-neutral-border',
          className
        )}
      >
        {prefix ? `${prefix} · ${mode}` : mode}
      </span>
    );
  }

  const Icon = modeIcons[normalized];

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-md border px-2 py-0.5',
        'text-[11px] font-semibold uppercase tracking-wide',
        modeStyles[normalized],
        className
      )}
    >
      {showIcon && <Icon className="h-3 w-3" aria-hidden="true" />}
      {prefix ? `${prefix} · ${normalized}` : normalized}
    </span>
  );
}
